import * as React from "react";
import {UpdateOperationType} from "../pages/config/ReasonCodesConfig";
import {ShippingRequestState} from "../pages/config/ShippingRequestState";
import Box from "@amzn/awsui-components-react-v3/polaris/box";
import {KeyValueProps} from "../pages/dms/common/KeyValue";
import {BOOKING_STATUS_FILTER, SHIPMENT_STATUS_FILTER} from "../pages/searchpage/SearchFilterConfig";
import {FlashbarProps} from "@amzn/awsui-components-react-v3/polaris/flashbar";
import {
    AWS_SCM_SHIPMENT_B_Y_BOOKING_CLASS,
    AWS_SCM_SHIPPING_REQUEST_CLASS,
    AWS_SCM_LOAD_SHIPPING_REQUEST_CLASS,
    AWS_SCM_SHIPMENT_RACK_BOOKING,
    AWS_SCM_SHIPMENT_DMS_BOOKING_CLASS,
    AWS_SCM_SHIPMENT_ASN_CLASS,
    AWS_SCM_SHIPMENT_STATUS_CLASS
} from "../pages/config/ShippingRequestClass";

export const MANDATORY_FIELD_MISSING_SUFFIX = ' field is mandatory';

export const GET_SHIPMENT_PACKAGE_BATCH_LIMIT = 30;

export const SHIPMENT_PACKAGES_DEFAULT_PAGE_SIZE = 10;

export const SHIPMENT_EVENT_DEFAULT_PAGE_SIZE = 50;

export const LINE_DELIMITER = '\n';

export const HYPHEN = '-';

export const FORM_FIELD_VALUE_REGEX = /^([0-9a-zA-Z-_]){0,32}$/;

export const UPLOAD_FILE_NAME_VALUE_REGEX = /^([0-9a-zA-Z-() _[\]]){0,128}\.(xlsm|pdf)$/;

export const UPLOAD_FILE_NAME_VALUE_ERR_MSG =
    <Box variant="small" padding="n" color="inherit">
        <Box variant="small" padding="n" color="inherit">File name doesn't comply with the guidelines.</Box>
            <ul>
                <li>Must consist of only uppercase, lowercase, numbers, SPACE,  '-' ,  '_' ,  '(' ,  ')' ,  '[' ,  ']' </li>
                <li>Must be shorter than 128 digits</li>
            </ul>
    </Box>;

export const MISSING_RATE_TICKET_ID = "missingLaneTicketId";

export const MISSING_ROUTE_TICKET_ID = "missingRouteTicketId";

export const CONFLICTING_ROUTES_TICKET_ID = "conflictingRoutesTicketId";

export const INELIGIBLE_BOOKING_LOCATION_TICKET_ID = "bookingLocationNotEnabledTicketId";

export const VALID_ON_HOLD_TICKET_PROPERTIES = [MISSING_ROUTE_TICKET_ID, CONFLICTING_ROUTES_TICKET_ID, MISSING_RATE_TICKET_ID, INELIGIBLE_BOOKING_LOCATION_TICKET_ID];

export type ReasonCode = 
  | "MissingLane"
  | "MissingRoute"
  | "ConflictingRoutes"
  | "BookingNotSupportedForSource"
  | "BookingNotSupportedForDestination"
  | "Pending Business Decision"
  | "Pending Documents"
  | "Manufacturing Defect (Pre-Shipping)"
  | "Rack Quality Defect Investigation (post-Shipping)"
  | "CDR - Project Cancelled"
  | "CDR - Redirect"
  | "CDR - Rack Quality Defect (HW Eng)"
  | "ACL - Rack Quality Defect (LSP/ODM Investigation)"
  | "ACL - Force Majeure"
  | "EDGE - Pending Delivery Appointment"
  | "DC - Rejection";

export interface ReasonCodeDescription {
    DESCRIPTION: string;
    ADDITIONAL_INFO?: string;
}

export const ON_HOLD_REASON_CODE_DESCRIPTION_MAP: Record<string, ReasonCodeDescription> = {
    "MissingLane": {
        DESCRIPTION: "Systematic Hold is applied because this lane doesn’t exist in AWS Transportation (AWST). ",
        ADDITIONAL_INFO: " Spot quote SLA is a 2 business days. Continuous shipment lane SLA is 5 business days."
    },
    "MissingRoute": {
        DESCRIPTION: "Systematic Hold is applied because this route doesn’t exist in AWS Transportation (AWST). "
    },
    "ConflictingRoutes": {
        DESCRIPTION: "Systematic Hold is applied because Conflicting routes found for the shipment in AWS Transportation (AWST)."
    },
    "BookingNotSupportedForSource": {
        DESCRIPTION: "Systematic Hold is applied because Booking support is not enabled for this source location in AWS Transportation (AWST). "
    },
    "BookingNotSupportedForDestination": {
        DESCRIPTION: "Systematic Hold is applied because Booking support is not enabled for this destination location in AWS Transportation (AWST). "
    },
    "Pending Business Decision": {
        DESCRIPTION: "Manual Hold is applied with Reason : Pending Business Decision."
    },
    "Pending Documents": {
        DESCRIPTION: "Manual Hold is applied with Reason : Pending Document."
    },
    "Manufacturing Defect (Pre-Shipping)": {
        DESCRIPTION: "Manual Hold is applied with Reason : Manufacturing Defect (Pre-Shipping)."
    },
    "Rack Quality Defect Investigation (post-Shipping)": {
        DESCRIPTION: "Manual Hold is applied with Reason : Rack Quality Defect Investigation (post-Shipping)."
    },
    "CDR - Project Cancelled": {
        DESCRIPTION: "Manual Hold is applied with Reason : CDR - Project Cancelled."
    },
    "CDR - Redirect": {
        DESCRIPTION: "Manual Hold is applied with Reason : CDR - Redirect."
    },
    "CDR - Rack Quality Defect (HW Eng)": {
        DESCRIPTION: "Manual Hold is applied with Reason : CDR - Rack Quality Defect (HW Eng)."
    },
    "ACL - Rack Quality Defect (LSP/ODM Investigation)": {
        DESCRIPTION: "Manual Hold is applied with Reason : ACL - Rack Quality Defect (LSP/ODM Investigation)."
    },
    "ACL - Force Majeure": {
        DESCRIPTION: "Manual Hold is applied with Reason : ACL - Force Majeure."
    },
    "EDGE - Pending Delivery Appointment": {
        DESCRIPTION: "Manual Hold is applied with Reason : EDGE - Pending Delivery Appointment."
    },
    "DC - Rejection": {
        DESCRIPTION: "Manual Hold is applied with Reason : DC - Rejection."
    }
};

// This is a fallback to handle null safety for reason Codes
export const DEFAULT_REASON_CODES = [
    { 
        id: 'PENDING_DOCUMENTS', 
        label: 'Pending Documents', 
        value: 'Pending Documents' 
    },
    { 
        id: 'PENDING_BUSINESS_DECISION', 
        label: 'Pending Business Decision', 
        value: 'Pending Business Decision' 
    },
    { 
        id: 'MANUFACTURING_DEFECT_PRE_SHIPPING', 
        label: 'Manufacturing Defect (Pre-Shipping)', 
        value: 'Manufacturing Defect (Pre-Shipping)' 
    },
    { 
        id: 'RACK_QUALITY_DEFECT_INVESTIGATION_POST_SHIPPING', 
        label: 'Rack Quality Defect Investigation (post-Shipping)', 
        value: 'Rack Quality Defect Investigation (post-Shipping)' 
    },
    { 
        id: 'CDR_PROJECT_CANCELLED', 
        label: 'CDR - Project Cancelled', 
        value: 'CDR - Project Cancelled' 
    },
    { 
        id: 'CDR_REDIRECT', 
        label: 'CDR - Redirect', 
        value: 'CDR - Redirect' 
    },
    { 
        id: 'CDR_RACK_QUALITY_DEFECT_HW_ENG', 
        label: 'CDR - Rack Quality Defect (HW Eng)', 
        value: 'CDR - Rack Quality Defect (HW Eng)' 
    },
    { 
        id: 'ACL_RACK_QUALITY_DEFECT_LSP_ODM_INVESTIGATION', 
        label: 'ACL - Rack Quality Defect (LSP/ODM Investigation)', 
        value: 'ACL - Rack Quality Defect (LSP/ODM Investigation)' 
    },
    { 
        id: 'ACL_FORCE_MAJEURE', 
        label: 'ACL - Force Majeure', 
        value: 'ACL - Force Majeure' 
    },
    { 
        id: 'EDGE_PENDING_DELIVERY_APPOINTMENT', 
        label: 'EDGE - Pending Delivery Appointment', 
        value: 'EDGE - Pending Delivery Appointment' 
    },
    { 
        id: 'DC_REJECTION', 
        label: 'DC - Rejection', 
        value: 'DC - Rejection' 
    }
];

export const REGEX_FAILURE_MESSAGE = (value: any) =>
    <Box variant="small" padding="n" color="inherit">
        <Box variant="small" padding="n" color="inherit">{value} doesn't comply with the guidelines.</Box>
            <ul>
                <li>Must consist of only uppercase, lowercase, numbers, UNDERSCORE(_) and HYPHEN('-')</li>
                <li>Must be shorter than 32 digits</li>
            </ul>
    </Box>;

export const DATE_FORMAT = 'YYYY/MM/DD';

export const DATE_FORMAT_HYPHEN = "YYYY-MM-DD";

export const DATE_TIME_FORMAT_HYPHEN = "YYYY-MM-DD HH:mm";

export const DATE_TIME_FORMAT_HYPHEN_UTC = "YYYY-MM-DD HH:mm UTC";

export const DATE_TIME_FORMAT = 'MMMM D, YYYY HH:mm:ss';

export const DATE_TIME_FORMAT_2 = 'YYYY-MM-DD hh:mmA';

export const SIMPLE_DATE_TIME_FORMAT = 'LL [at] LT';

export const EDIT_ASN_PAGE = {
    LOADER_MESSAGE: 'edit page'
};

export const ENTER_KEY = 'Enter';

export const DOWNLOAD_FILE_ERROR = 'We couldn\'t upload this file. Ensure file meets requirements.';

export const SRC_LOCATION_REF = "Origin";

export const DEST_LOCATION_REF = "Destination";

export const BANNER_TYPE_INFO: FlashbarProps.Type = "info";

export const BANNER_TYPE_WARNING: FlashbarProps.Type = "warning";

export const BANNER_TYPE_SUCCESS: FlashbarProps.Type = "success";

export const BANNER_TYPE_ERROR: FlashbarProps.Type = "error";

export const UPLOAD_PAGE_SHIPPING_DETAILS = {
    ASN_HEADER: 'Enter shipping details',
    BOOKING_HEADER: 'Enter shipping details',
    TRANSPORTATION_DETAILS_HEADER: 'Enter transportation details',
    ADDITIONAL_DETAILS_HEADER: 'Enter additional details',
    UPDATE_REASONS_HEADER: 'Enter reason details',
    NO_LOCATION_FOUND_MESSAGE: 'No matching location found',
    EMPTY_LOCATIONS_RETURNED_BY_BACKEND: 'No locations',
    LOADING_SRC_LOCATION_DROP_DOWN: 'Fetching the list of source locations',
    LOADING_DEST_LOCATION_DROP_DOWN: 'Fetching the list of destination locations',
    ERROR_SRC_LOCATION_DROP_DOWN: 'Error while fetching source location',
    ERROR_DEST_LOCATION_DROP_DOWN: 'Error while fetching destination location',
    EMPTY_CARRIER_RETURNED_BY_BACKEND: 'No Carrier Data',
    EMPTY_ORDER_RETURNED_BY_BACKEND: 'No Order Data',
    ERROR_CARRIER_DATA_DROP_DOWN: 'Error while fetching Carrier Details',
    LOADING_CARRIER_DATA_DROP_DOWN: 'Fetching the list of carrier',
    ERROR_ORDER_DATA_DROP_DOWN: 'Error while fetching Order Details',
    LOADING_ORDER_DATA_DROP_DOWN: 'Fetching the order type list',
    NO_ORDER_TYPE_FOUND_MESSAGE: 'No matching order type found',
    NO_EQUIPMENT_TYPE_FOUND_MESSAGE: 'No matching equipment type found',
    NO_CARRIER_FOUND_MESSAGE: 'No matching carrier found, please select "Other" carrier',
    NO_INCOTERM_FOUND_MESSAGE: 'No matching incoterm found',
    NO_PAYMENT_METHOD_FOUND_MESSAGE: 'No matching payment method found',
    NO_TRANSPORTATION_MODE_FOUND_MESSAGE: 'No matching mode of transportation found',
    NO_SHIPPING_SPEED_FOUND_MESSAGE: 'No matching shipping speed item found',
    NO_BONDED_FLAG_FOUND_MESSAGE: 'No matching bonded flag item found'
};

export const SEARCH_SHIPMENTS = {
    LOADING_SUPPLIER_DROP_DOWN: 'Fetching the list of suppliers',
    LOADING_CLUSTER_DROP_DOWN: 'Fetching the list of Clusters',
    LOADING_SRC_LOCATION_DROP_DOWN: 'Fetching the list of source locations',
    LOADING_DEST_LOCATION_DROP_DOWN: 'Fetching the list of destination locations',
    LOADING_AZ_DROP_DOWN: 'Fetching the list of AZs',
    SORT_DESC: "DESC",
    SORT_ASC: "ASC",
    PROPERTIES_PATH: 'properties',
    MAX_SUPPORTED_HITS: 10000
};

export const DOWNLOAD_SHIPMENTS = {
    MAX_SUPPORTED_DOWNLOADS: 20000,
    POLLING_INTERVAL: 2000,
    POLLING_TIMEOUT: 5 * 60 * 1000,
    DOWNLOAD_SUCCESS_MESSAGE: "Your download has been completed successfully!",
    DOWNLOAD_STATUS: {
        REQUESTED:'REQUESTED', IN_PROGRESS:'IN_PROGRESS', SUCCESS: 'SUCCESS', FAILED: 'FAILED'
    }
};

export const UPLOAD_PAGE_ADDITIONAL_DETAILS = {
    HEADER: <Box variant="h2"> Enter additional details <i>- optional</i> </Box>
};

export const UPLOAD_PAGE_UPLOAD_PKG_DETAILS = {
    HEADER: 'Upload packaging details'
};

export const USER_DISPLAY_STRINGS = {
    SHIPMENT_PACKAGING_DETAILS: {
        LOADER_MESSAGE: 'shipment packaging details'
    },
    SHIPMENT_EVENT_PAGE: {
        LOADER_MESSAGE: 'shipment events page'
    },
    SHIPMENT_PACKAGE: {
        LOADER_MESSAGE: 'Loading shipment packages',
        ERROR_MESSAGE: 'An error occured while fetching the shipment packaging details for this request. Please refresh the page.',
        DATA_INCONSISTENT_ERROR_MESSAGE: 'We are still populating the packaging details, please refresh after some time.',
        EMPTY_MESSAGE: 'No shipment packages found for this request.',
        PACKAGE_NOT_FOUND_ERROR_CODE: 'NF'
    },
    SHIPMENT_ITEM: {
        LOADER_MESSAGE: 'Loading shipment items',
        ERROR_MESSAGE: 'An error occured while fetching the shipment items for this request. Please refresh the page.',
        EMPTY_MESSAGE: 'No shipment items found for this request.'
    },
    SHIPMENT_EVENT: {
        LOADER_MESSAGE: 'Loading shipment events',
        ERROR_MESSAGE: 'An error occured while fetching the shipment events for this request. Please refresh the page.',
        EMPTY_MESSAGE: 'No shipment events found.'
    },
    SHIPMENT_LOAD_DETAILS: {
        ERROR_MESSAGE: 'An error occured while fetching the order package tracking details for this request. Please refresh the page.',
        ACCESS_DENIED_MESSAGE: 'User does not have access to the requested resource!',
        DATA_INCONSISTENT_ERROR_MESSAGE: 'We are still populating the order package tracking details, please refresh after some time.',
        LOAD_BOOKING_ACCESS_DENIED_MESSAGE: 'User does not have access to view all the child bookings.'
    }
};

export const ORDER_PACKAGE_TRACKING_DETAILS_DISPLAY_STRINGS={
  FILTER_MESSAGE:{
    EMPTY_MESSAGE: 'No child bookings found for this request'
  },
  TABLE_HEADER:{
    TITLE:"Order package tracking details",
    COLUMN_LAYOUT:{
        PURCHASE_ORDER_NUMBER:'Purchase order number',
        OUTERMOST_PACKAGES:'Outermost packages',
        TRACKING_STATUS:'Tracking status'
    }
  },
  TABLE_FOOTER:{
    SHOW_ALL:"Show all child bookings",
    SHOW_LESS:"Show less"
  },
  TOGGLE_HEADER:{
      HEADER:"Show Cancelled bookings",
  }
};

export const TABLE_DATA = {
    NOT_AVAILABLE: HYPHEN,
    FILTER_PLACE_HOLDER: 'Search',
    WRAP_LINES: 'Wrap lines',
    WRAP_DESCRIPTION: 'Enable to wrap table cell content, disable to truncate text.',
    PAGE: {
        SIZE: 'Page Size'
    },
    PREFERENCES: {
        TITLE: 'Preferences',
        CONFIRM: 'Confirm',
        CANCEL: 'Cancel',
        VISIBLE_COLUMNS:{
          TITLE:'Select visible columns',
          LABEL:'Properties'
        }
    },
    HEADER: {
        SHIPMENT_PACKAGES_MESSAGE: 'Shipment packages',
        SHIPMENT_ITEMS_MESSAGE: 'Shipment items',
        SHIPMENT_EVENTS_MESSAGE: 'Shipment events',
        SHIPMENT_PACKAGES_FILTER_MESSAGE: 'Only top level packages shown by default. Use the toggle on the right to see entire list of packages in shipment.',
        SHIPMENT_ITEMS_FILTER_MESSAGE: 'Search',
        SHIPMENT_EVENTS_FILTER_MESSAGE: 'Use filter to search any record in shipment events table'
    }
};

export const SHIPPING_DETAILS = {
    HEADER: 'Shipping details',
    LOADER_MESSAGE: 'shipping details',
    STATUS: 'Status',
    ASN_STATUS: 'Asn status',
    SHIPMENT_STATUS: 'Shipment status',
    SUPPLIER_SHIPMENT_ID: 'Supplier shipment ID',
    REQUESTER: 'Requester',
    PROGRAM: 'Program',
    SHIPPER: 'Shipper',
    CONSIGNEE: 'Consignee',
    MODE_OF_TRANSPORT: 'Transportation mode',
    SERVICE_LEVEL: 'Service level',
    ROUTE_TYPE: 'Route type',
    SOURCE_LOCATION: 'Ship from',
    DESTINATION_LOCATION: 'Ship to',
    REQUESTED_PICKUP_TIME: 'Requested pickup time',
    REQUESTED_DELIVERY_TIME: 'Requested delivery time',
    INCOTERM: 'Incoterm',
    PAYMENT_METHOD: 'Payment method',
    BONDED_FLAG: 'Bonded flag',
    SHIPMENT_SECURITY_LEVELS: 'Shipment security level',
    EQUIPMENT_TYPE: 'Equipment type',
    HANDLING_INSTRUCTIONS: 'Handling instructions',
    PURCHASE_ORDER_TYPE: 'Purchase order type',
    PACKAGING_REFERENCE_TYPE: 'packagingReferenceType',
    PROPERTY_IS_ASN_RECEIVED: 'isAsnReceived',
    PROPERTY_IS_SHIPMENT_STATUS_UPDATE_RECEIVED: 'isShipmentStatusUpdateReceived',
    PROPERTY_IS_TMS_ONBOARDED: "isTmsOnboardedBooking",
    PROPERTY_MIGRATION_TYPE: "migrationType",
    ACTIONS: 'Actions',
    DOWNLOAD: 'Download',
    DOWNLOAD_ERRORS: 'Download errors',
    DOWNLOAD_WARNINGS: 'Download warnings',
    DOWNLOAD_PACKAGE_REFERENCE: 'Packaging details file',
    SHIPMENT_STATUS_LIFE_CYCLE_GRAPH_ID: 'AWS_SCM_SHIPMENT_STATUS_GRAPH_ID',
    SHIPMENT_ASN_LIFE_CYCLE_GRAPH_ID: 'AWS_SCM_SHIPMENT_ASN_GRAPH_ID',
    EXECUTION_DETAIL_STATE_PENDING: 'PENDING',
    EXECUTION_DETAIL_STATE_ABORTED: 'ABORTED',
    EXECUTION_DETAIL_STATE_VALIDATION_ERROR: 'VALIDATION_ERROR',
    EXECUTION_DETAIL_STATE_VALIDATION_SUCCESS_WITH_WARNING: 'VALIDATION_SUCCESS_WITH_WARNING',
    EXECUTION_DETAIL_STATE_VALIDATION_SUCCESS: 'VALIDATION_SUCCESS',
    EXECUTION_DETAIL_STATE_IN_PROGRESS: 'IN_PROGRESS',
    EXECUTION_DETAIL_STATE_IN_PROGRESS_WITH_WARNING: 'IN_PROGRESS_WITH_WARNING',
    EXECUTION_DETAIL_COMPLETED_WITH_WARNING: 'COMPLETED_WITH_WARNING',
    EXECUTION_DETAIL_COMPLETED: 'COMPLETED',
    EXECUTION_DETAIL_TYPE_CREATE: 'CREATE',
    EXECUTION_DETAIL_TYPE_UPDATE: 'UPDATE',
    EXECUTION_DETAIL_TYPE_CANCEL: 'CANCEL',
    EXECUTION_DETAIL_TYPE_ASN_UPLOAD: 'ASN_UPLOAD',
    EXECUTION_DETAIL_TYPE_RESUME: 'RESUME',
    EXECUTION_DETAIL_TYPE_HOLD: 'HOLD',
    EXECUTION_DETAIL_TYPE_EXPEDITE_SHIPMENT: 'SERVICE_LEVEL_UPGRADE',
    EXPEDITE_ROUTE_TYPE: 'ROUTE_TYPE_EXPEDITE',
    EXPEDITE_SERVICE_LEVEL: 'SERVICE_LEVEL_EXPEDITE',
    ROUTE_TYPE_EXPRESS: "EXPRESS",
    ROUTE_TYPE_STANDARD: "STANDARD",
    UPDATE_OPERATION_TYPE_UPDATE: 'UPDATE',
    UPDATE_OPERATION_TYPE_SERVICE_LEVEL_UPDATE: 'SERVICE_LEVEL_UPGRADE',
    UPDATE_OPERATION_TYPE_ROUTE_TYPE: 'ROUTE_TYPE',
    UPDATE_OPERATION_TYPE_ASN_UPLOAD: 'ASN_UPLOAD',
    CREATED_ON: 'Created on',
    LATEST_UPDATED_ON: 'Latest updated on',
    LATEST_UPDATED_BY: 'Latest updated by',
    LATEST_UPDATE_TYPE: 'Latest update type',
    REASON_CODE: 'Reason code',
    SERVICE_LEVEL_EXPRESS: 'EXPRESS',
    CANCELED_BY: 'Canceled by',
    EXPEDITED_BY: 'Expedited by',
    ROUTE: 'Route',
    ON_HOLD_TICKET_LINK: 'On-hold Ticket',
    PARENT_BOOKING_DETAILS: 'Parent booking details',
    ASN_COMMUNICATION_DETAIL_PACKAGING_REFERENCE: 'UI_ASN',
    BOOKING_COMMUNICATION_DETAIL_PACKAGING_REFERENCE: 'UI_BOOKING',
    COMMUNICATION_DETAIL_VALIDATION_FAILURE_STATE: 'validationErrors',
    BOOKING_PACKAGING_REFERENCE_STATES: ['BOOKING_REQUEST', 'UI_BOOKING'],
    EXECUTION_ORDER_PACKAGING_REFERENCE_STATES: ['EXECUTION_ORDER'],
    ASN_PACKAGING_REFERENCE_STATES: ['ASN', 'UI_ASN'],
    PACKAGING_REFERENCE_STATES: ['ASN', 'BOOKING_REQUEST', 'UI_ASN', 'UI_BOOKING'],
    MIGRATION_TYPE_PROPERTIES: ['shipment_plan_to_tms', 'shipment_plan_to_tms_pending_lsp_update']
};

export const EXECUTION_DETAIL_TYPE_CREATE_AND_UPDATE = [
    SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_CREATE,
    SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_UPDATE
];

export const BOOKING_EXECUTION_TYPE_CONTAINING_PACKAGING_DETAILS = [
    SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_CREATE,
    SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_ASN_UPLOAD
];

export const UPDATE_PARAMETERS = new Map([
    ['destinationLocation', 'Ship to'],
    ['sourceLocation', 'Ship from'],
    ['consigneeReference', 'Consignee'],
    ['pickUpTime', 'Pickup time'],
    ['deliveryTime', 'Delivery time'],
    ['serviceLevel', 'Service level'],
    ['modeOfTransport', 'Transportation mode'],
    ['requestedEquipmentType', 'Equipment type'],
    ['paymentMethod', 'Payment method'],
    ['incoterm', 'Incoterm'],
    ['shippingOffer', 'Shipping offer'],
    ['orderType', 'Purchase order type'],
    ['clientReferenceId', 'Supplier shipment ID'],
    ['requestType', 'Request type'],
    ['shipperReference', 'Shipper'],
    ['routeType', 'Route type']
]);

export const PENDING_BANNER_DETAILS = {
    PENDING_CANCEL_HEADER: "Pending cancellation",
    PENDING_CANCEL_CONTENT: "A cancel request has been submitted. It will be processed shortly.",
    PENDING_CANCEL_BANNER_TYPE: BANNER_TYPE_INFO,
    PENDING_UPDATE_HEADER: "Pending update",
    PENDING_UPDATE_CONTENT: "An update request has been submitted. It will be processed once the original booking is accepted.",
    PENDING_UPDATE_BANNER_TYPE: BANNER_TYPE_INFO,
    PENDING_ASN_UPLOAD_HEADER: "Validating ASN request",
    PENDING_ASN_UPLOAD_CONTENT: "Please remain on this page and don’t submit another ASN until this action is complete.",
    BANNER_TYPE: BANNER_TYPE_INFO,
    UPDATE_HEADER: "Pending Update request",
    UPDATE_CONTENT: "Please wait while we are validating the update request."
};

export const VALIDATION_SUCCESS_BANNER_DETAILS = {
    ASN_UPLOAD_HEADER: "ASN validation complete",
    ASN_UPLOAD_CONTENT: "Please don’t submit another ASN until your request is processed.",
    BANNER_TYPE: BANNER_TYPE_INFO,
    UPDATE_HEADER: "Update validation complete",
    UPDATE_CONTENT: "Update request has been validated successfully. Please wait while it is being processed."
};

export const VALIDATION_SUCCESS_WITH_WARNING_BANNER_DETAILS = {
    ASN_UPLOAD_HEADER: "ASN validation complete",
    ASN_UPLOAD_CONTENT: (requestId: string) => `The ASN is validated with warnings. You can download the report to view warnings. Use shipment reference number ${requestId} for tracking.`,
    BANNER_TYPE: BANNER_TYPE_WARNING,
    UPDATE_HEADER: "Update validation complete with warnings",
    UPDATE_CONTENT: (requestId: string) => `Update request has been validated successfully with warnings. You can download the report to view warnings. Use shipment reference number ${requestId} for tracking.`
};

export const PROCESSING_BANNER_DETAILS = {
    ASN_UPLOAD_HEADER: "Processing ASN request",
    ASN_UPLOAD_CONTENT: "Please remain on this page and don’t submit another ASN until this action is complete.",
    BANNER_TYPE: BANNER_TYPE_INFO,
    UPDATE_HEADER: "Processing Update request",
    UPDATE_CONTENT: "Update request is being processed."
};

export const VEHICLE_PROCESSING_BANNER_DETAILS = {
    HEADER: "Processing Update request",
    CONTENT: "The following booking is in update state. Please wait for the update to be processed before making any change.",
    BANNER_TYPE: BANNER_TYPE_INFO
};

export const VEHICLE_STATES = {
    PROCESSING_UPDATE: "PROCESSING_UPDATE"
};

export const VEHICLE_POLLING = {
    POLLING_INTERVAL: 30 * 1000,
    POLLING_TIMEOUT: 30 * 60 * 1000
};

export const PROCESSING_WITH_WARNING_BANNER_DETAILS = {
    ASN_UPLOAD_HEADER: "Processing ASN request",
    ASN_UPLOAD_CONTENT: "Please remain on this page and don’t submit another ASN until this action is complete.",
    BANNER_TYPE: BANNER_TYPE_WARNING,
    UPDATE_HEADER: "Processing Update request",
    UPDATE_CONTENT: "Update request is being processed. You can download the report to view warnings."
};

export const COMPLETED_BANNER_DETAILS = {
    ASN_UPLOAD_HEADER: "ASN request processed",
    ASN_UPLOAD_CONTENT: (requestId: string) => `Use shipment reference number ${requestId} for tracking.`,
    BANNER_TYPE: BANNER_TYPE_SUCCESS,
    UPDATE_HEADER: "Update request processed",
    UPDATE_CONTENT: (requestId: string) => `Use shipment reference number ${requestId} for tracking.`
};

export const COMPLETED_WITH_WARNING_BANNER_DETAILS = {
    ASN_UPLOAD_HEADER: "ASN request processed",
    ASN_UPLOAD_CONTENT: (requestId: string) => `Your request is processed with warnings. You can download the report to view warnings. Use shipment reference number ${requestId} for tracking.`,
    BANNER_TYPE: BANNER_TYPE_WARNING,
    UPDATE_HEADER: "Update request processed",
    UPDATE_CONTENT: (requestId: string) => `Your request is processed with warnings. You can download the report to view warnings. Use shipment reference number ${requestId} for tracking.`
};

export const VALIDATION_ERROR_BANNER_DETAILS = {
    ASN_UPLOAD_HEADER: "This ASN request couldn’t be validated",
    ASN_UPLOAD_CONTENT: "Download the file to address validation errors, and try again.",
    BANNER_TYPE: BANNER_TYPE_ERROR,
    UPDATE_HEADER: "This Update request couldn’t be validated",
    UPDATE_CONTENT: "Download the file to address validation errors, and try again."
};

export const WARNING_STATES = [
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_VALIDATION_SUCCESS_WITH_WARNING,
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_IN_PROGRESS_WITH_WARNING,
    SHIPPING_DETAILS.EXECUTION_DETAIL_COMPLETED_WITH_WARNING
];

export const TERMINAL_STATES = [
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_VALIDATION_ERROR,
    SHIPPING_DETAILS.EXECUTION_DETAIL_COMPLETED_WITH_WARNING,
    SHIPPING_DETAILS.EXECUTION_DETAIL_COMPLETED
];

export const IN_PROGRESS_AND_VALIDATION_SUCCESS_STATES = [
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_IN_PROGRESS,
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_IN_PROGRESS_WITH_WARNING,
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_VALIDATION_SUCCESS,
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_VALIDATION_SUCCESS_WITH_WARNING
];

export const NON_TERMINAL_EXECUTION_STATES = [
  SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_PENDING,
  SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_IN_PROGRESS,
  SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_IN_PROGRESS_WITH_WARNING,
  SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_VALIDATION_SUCCESS,
  SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_VALIDATION_SUCCESS_WITH_WARNING
];

export const BOOKING_MODIFICATION_EXECUTION_STATES = [
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_VALIDATION_ERROR,
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_IN_PROGRESS,
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_IN_PROGRESS_WITH_WARNING,
    SHIPPING_DETAILS.EXECUTION_DETAIL_COMPLETED_WITH_WARNING,
    SHIPPING_DETAILS.EXECUTION_DETAIL_COMPLETED
];

export const NO_POLLING_SHIPPING_REQUEST_STATUSES = [
  ShippingRequestState.ERROR,
  ShippingRequestState.FAILURE,
  ShippingRequestState.CREATED,
  ShippingRequestState.CANCELLED,
  ShippingRequestState.ACCEPTED,
  ShippingRequestState.BOOKING_REJECTED,
  ShippingRequestState.COMPLETED,
  ShippingRequestState.MANUALLY_COMPLETED
];

export const DOCUMENT_UPLOAD_DISABLED_STATES = [
  ShippingRequestState.CANCELLED,
  ShippingRequestState.COMPLETED,
  ShippingRequestState.PENDING_ASSIGNMENT,
  ShippingRequestState.REDIRECTED,
  ShippingRequestState.MANUALLY_COMPLETED,
]

export const CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES = [
  ShippingRequestState.ON_HOLD,
  ShippingRequestState.PENDING_ASSIGNMENT,
  ShippingRequestState.REDIRECTED,
  ShippingRequestState.SHIPMENT_PLANNED,
  ShippingRequestState.LOAD_TENDERED,
  ShippingRequestState.LOAD_TENDER_CANCELLED,
  ShippingRequestState.BOOKING_REQUESTED,
  ShippingRequestState.BOOKING_ACCEPTED,
  ShippingRequestState.COMPLIANCE_HOLD
];

export const CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_LAST_MILE_BOOKING = [
    ShippingRequestState.ON_HOLD,
    ShippingRequestState.PENDING_ASSIGNMENT,
    ShippingRequestState.REDIRECTED,
    ShippingRequestState.SHIPMENT_PLANNED, 
    ShippingRequestState.IN_PROGRESS,
    ShippingRequestState.COMPLETED,
    ShippingRequestState.MANUALLY_COMPLETED,
    ShippingRequestState.CANCELLED
];

export const CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_RACK_BOOKING = [
    ShippingRequestState.ON_HOLD,
    ShippingRequestState.PENDING_ASSIGNMENT,
    ShippingRequestState.REDIRECTED,
    ShippingRequestState.SUBMITTED,
    ShippingRequestState.SHIPMENT_PLANNED,
    ShippingRequestState.COMPLIANCE_HOLD,
    ShippingRequestState.PROCESSING,
    ShippingRequestState.LOAD_TENDERED,
    ShippingRequestState.BOOKING_REQUESTED,
    ShippingRequestState.BOOKING_ACCEPTED,
    ShippingRequestState.COMPLETED,
    ShippingRequestState.MANUALLY_COMPLETED,
    ShippingRequestState.CANCELLED
];

export const CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_LOAD_BOOKING = [
    ShippingRequestState.SUBMITTED,
    ShippingRequestState.TENTATIVE,
    ShippingRequestState.PROCESSING
];

export const CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_WAREHOUSE_BOOKING = [
    ShippingRequestState.SUBMITTED
]

export const UPDATE_POLLING_STATES = [
    ShippingRequestState.BOOKING_REQUESTED
];

export const NON_EDITABLE_STATES = [
    'Error',
    'Cancelled'
];

export const COMPLETED_STATES = [
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_ABORTED,
    SHIPPING_DETAILS.EXECUTION_DETAIL_COMPLETED,
    SHIPPING_DETAILS.EXECUTION_DETAIL_COMPLETED_WITH_WARNING
];

export const IN_PROGRESS_AND_COMPLETED_STATES = [
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_IN_PROGRESS,
    SHIPPING_DETAILS.EXECUTION_DETAIL_STATE_IN_PROGRESS_WITH_WARNING,
    ...COMPLETED_STATES
];

export const BOOKING_PORTAL_SHIPPING_REQUEST_CLASSES = [
    AWS_SCM_SHIPMENT_B_Y_BOOKING_CLASS, 
    AWS_SCM_SHIPPING_REQUEST_CLASS, 
    AWS_SCM_LOAD_SHIPPING_REQUEST_CLASS,
    AWS_SCM_SHIPMENT_RACK_BOOKING
];

export const BOOKING_PORTAL_SHIPPING_REQUEST_CLASSES_EXCLUDING_LOAD_BOOKINGS = [
    AWS_SCM_SHIPMENT_B_Y_BOOKING_CLASS,
    AWS_SCM_SHIPPING_REQUEST_CLASS,
    AWS_SCM_SHIPMENT_RACK_BOOKING
];

export const SHIPPING_REQUEST_CLASSES_EXCLUDING_LOAD_BOOKINGS = [
    AWS_SCM_SHIPMENT_B_Y_BOOKING_CLASS,
    AWS_SCM_SHIPMENT_DMS_BOOKING_CLASS,
    AWS_SCM_SHIPMENT_ASN_CLASS,
    AWS_SCM_SHIPMENT_STATUS_CLASS,
    AWS_SCM_SHIPPING_REQUEST_CLASS,
    AWS_SCM_SHIPMENT_RACK_BOOKING
];

export const SHIPMENT_REFERENCES = {
    HEADER: 'Shipment reference',
    LOADER_MESSAGE: 'shipment references',
    PURCHASE_ORDER_NUMBER: 'Purchase order number',
    SALES_ORDER_NUMBER: 'Sales order number',
    PACKING_LIST_NUMBER: 'Packing list number',
    COMMERCIAL_INVOICE: 'Commercial invoice',
    CARRIER_REFERENCE_NUMBER: 'Carrier reference number',
    DELIVERY_ACCESS_ID: 'Delivery Access ID',
    DELIVERY_INSTRUCTIONS: 'Delivery Instructions'
};

export const SHIPMENT_REFERENCE_TYPE = {
    SALES_ORDER_NUMBER: 'salesOrderNumber',
    PACKING_LIST_NUMBER: 'packingListNumber',
    CARRIER_REFERENCE_NUMBER: 'carrierReferenceNumber',
    DELIVERY_ACCESS_ID: 'deliveryAccessId',
    DELIVERY_INSTRUCTIONS: 'deliveryInstructions'
};

export const SHIPMENT_TRACKING = {
    HEADER: 'Shipment tracking details',
    LOADER_MESSAGE: 'tracking details',
    CARRIER: 'Carrier',
    TRACKING_NUMBER: 'Tracking number',
    ESTIMATED_PICKUP_TIME: 'Estimated pickup time',
    ACTUAL_PICKUP_TIME: 'Actual pickup time',
    ESTIMATED_DELIVERY_TIME: 'Estimated delivery time',
    ACTUAL_DELIVERY_TIME: 'Actual delivery time',
    VIEW_EVENTS_BUTTON_TITLE: 'Tracking details',
    PLANNER_HISTORY: 'Planner History',
    EDIT_SHIPMENT: 'Edit shipment',
    CANCEL_SHIPMENT: 'Cancel shipment',
    SHIPMENT_AUDIT_HISTORY: 'Shipment Audit History',
    SHIPMENT_EVENTS: 'Shipment Events',
    NO_SHIPMENT_EVENT_RECEIVED: 'No Shipment Event Received'
};

export const SHIPMENT_ITEM_PROPERTY = {
    IPN: 'ipn',
    PROJECT_ID: 'projectId'
};

export const DELIVERY_INSTRUCTION: Record<string, KeyValueProps> = {
    "UNP": {value: "UNP", label: "Unpacking required"},
    "DIS": {value: "DIS", label: "Disposal of packing material required"},
    "LFD": {value: "LFD", label: "To leave delivery at front desk"},
};

export const DELIVERY_INSTRUCTIONS: KeyValueProps[] = [
  DELIVERY_INSTRUCTION.UNP,
  DELIVERY_INSTRUCTION.DIS,
  DELIVERY_INSTRUCTION.LFD,
];

export const OPERATIONS_TO_SKIP_REASON = [
    UpdateOperationType.DELIVERY_ACCESS_ID,
    UpdateOperationType.DELIVERY_INSTRUCTIONS
];

/**
 * This Config would be used to map Shipment Event Type to Shipment Event Type Info for rendering on UI.
 */
export const SHIPMENT_EVENT_TYPE_DESCRIPTION : { [key:string]:Function; } = {
    'SIS': (eventTime : string) => `Origin booking received`,
    'CBK': (eventTime : string) => `Origin booking accepted`,
    'PUP': (eventTime : string) => 'Pick up from origin',
    'BKB': (eventTime : string) => 'Booking with airline',
    'RDD': (eventTime : string) => 'Requested delivery due date',
    'ADD': (eventTime : string) => `Received expected delivery date as ${eventTime}`,
    'ETF': (eventTime : string) => `Received expected delivery date for intermediate location as ${eventTime}`,
    'ATH': (eventTime : string) => 'Arrived at intermediate transfer hub',
    'BKC': (eventTime : string) => 'Shipment Package booking authorized',
    'DTH': (eventTime : string) => 'Departure transfer hub',
    'REW': (eventTime : string) => 'Received at export warehouse',
    'DEW': (eventTime : string) => 'Departed from export warehouse',
    'REH': (eventTime : string) => 'Received at export hub',
    'IOK': (eventTime : string) => 'Inspection passed',
    'DEP': (eventTime : string) => 'Departure from origin airport',
    'ARR': (eventTime : string) => 'Arrival at destination airport',
    'NFD': (eventTime : string) => 'Documents provided for customs clearance',
    'TPN': (eventTime : string) => 'Documents tendered to a 3rd party broker for clearance',
    'RIW': (eventTime : string) => 'Received at import warehouse',
    'DNL': (eventTime : string) => 'Placed on Hold',
    'CCD': (eventTime : string) => 'Customs cleared',
    'DRB': (eventTime : string) => 'Documents released to customs broker',
    'GFC': (eventTime : string) => 'Goods in free circulation (mainly in Europe)',
    'FOH': (eventTime : string) => 'Freight on hand and prepped for flight',
    'OFD': (eventTime : string) => 'Out for delivery',
    'POD': (eventTime : string) => 'Proof of delivery obtained',
    'ETA': (eventTime : string) => `Received expected delivery date for intermediate location as ${eventTime}`,
    'ACI': (eventTime : string) => 'Outside broker customs cleared',
    'NDL': (eventTime : string) => 'Returned to origin for rework',
    'COM': (eventTime : string) => 'Comments received',
    'QCT': (eventTime : string) => 'Quality deviation code received',
    'N/A': (eventTime : string) => 'Unknown event',
    'DCM': (eventTime : string) => 'Documents Missing',
    'DCR': (eventTime : string) => 'Documents Received',
    'CDR': (eventTime : string) => 'Final Documents Received',
    'ETD': (eventTime : string) => `Received Estimated Time of Departure for intermediate location as ${eventTime}`,
    'DDP': (eventTime : string) => 'Duties and Taxes Paid',
    'SDV': (eventTime : string) => 'Self-delivered to the forwarder warehouse or terminal',
    'SPC': (eventTime : string) => 'Self-pickup by consignee instead of delivery',
    'COK': (eventTime : string) => 'Bill of Entry approved by Authority',
    'RPK': (eventTime : string) => 'Cargo is indicated ready by supplier',
    'ADF': (eventTime : string) => 'Delivery appointment was scheduled',
    'PDD': (eventTime : string) => 'Scheduled appointment to deliver',
    'ECD': (eventTime : string) => 'Cargo cleared for export from origin',
    'NPD': (eventTime : string) => 'Shipment cancelled before pickup'
};

export const QUALITY_DEVIATION_CODE_FOR_CUSTOMS = ["DIS114690", "DOC113190", "DOC113294", "DOC113590", "DOC111590", "DEL024490", "DEL024790"]

export const SHIPMENT_EVENT_TYPE: Record<string, string> = {
    CBK: 'CBK',
    RPK: 'RPK',
    PUP: 'PUP',
    SDV: 'SDV',
    ECD: 'ECD',
    DEP: 'DEP',
    CCD: 'CCD',
    ACI: 'ACI',
    ARR: 'ARR',
    QCT: 'QCT',
    POD: 'POD'
}

export enum SHIPMENT_TRACKER_STATES {
    READY_FOR_PICKUP = "Ready For Pickup",
    PICKED_UP = "Picked Up",
    DEPARTED_ORIGIN = "Departure From Origin",
    ARRIVED_AT_DESTINATION = "Arrival at Destination Hub"
}

export const SHIPMENT_EVENT_TYPE_DESCRIPTION_FOR_TRACKER: { [key:string]:Function; } = {
    'CBK' : (comment : string | undefined) =>  'Origin booking accepted',
    'RPK' : (comment : string | undefined) =>  'Cargo is Ready For Pick up From Supplier',
    'PUP' : (comment : string | undefined) =>  'Cargo is Picked up from origin',
    'SDV' : (comment : string | undefined) =>  'Cargo is Self-delivered to the forwarder warehouse or terminal',
    'ECD' : (comment : string | undefined) =>  'Customs cleared for Cargo from Origin Country',
    'DEP' : (comment : string | undefined) =>  'Cargo Departure from origin airport',
    'CCD' : (comment : string | undefined) =>  'Customs cleared for Cargo from Destination Country',
    'ACI' : (comment : string | undefined) =>  'Customs cleared for Cargo from Destination Country',
    'ARR' : (comment : string | undefined) =>  'Arrived at Destination Airport',
    'POD' : (comment : string | undefined) =>  'Cargo is delivered',
    'QCT' : (comment : string | undefined) => comment
}

/**
 * This Config would be used to map Shipment Event Type to source or destination location timezones for rendering dates on UI.
 */
export const SHIPMENT_EVENT_TO_LOCATION_REFERENCE_MAPPING : { [key:string]:string; } = {
    'CBK': SRC_LOCATION_REF,
    'RPK': SRC_LOCATION_REF,
    'PUP': SRC_LOCATION_REF,
    'REW': SRC_LOCATION_REF,
    'BKB': SRC_LOCATION_REF,
    'ETD': SRC_LOCATION_REF,
    'ETA': SRC_LOCATION_REF,
    'DEP': SRC_LOCATION_REF,
    'ECD': SRC_LOCATION_REF,
    'RDD': SRC_LOCATION_REF,
    'ADD': DEST_LOCATION_REF,
    'ARR': DEST_LOCATION_REF,
    'RIW': DEST_LOCATION_REF,
    'DRB': DEST_LOCATION_REF,
    'CCD': DEST_LOCATION_REF,
    'ACI': DEST_LOCATION_REF,
    'OFD': DEST_LOCATION_REF,
    'POD': DEST_LOCATION_REF
};

export enum CUSTOMS_STATUS_MESSAGE {
    CUSTOMS_CLEARED = "Customs Cleared",
    CUSTOMS_PENDING = "Customs Pending",
    CUSTOMS_STUCK = "Stuck in Customs"
}

/**
 * This Config builds a hyperlink to the TT template for raising System issues.
 */
const CONTACT_DEV_TEAM_MESSAGE =
    (<Box padding="n" color="inherit">
        Our engineers are looking into this.
    </Box>);

export const ACCESS_DENIED_MESSAGE =
    (<Box padding="n" color="inherit">
        User does not have access to the requested resource!
    </Box>);

export const UPLOAD_ASN_ACCESS_DENIED_MESSAGE =
    (<Box padding="n" color="inherit">
        User does not have access to upload an ASN!
    </Box>);

export const CREATE_BOOKING_ACCESS_DENIED_MESSAGE =
    (<Box padding="n" color="inherit">
        User does not have access to create a booking!
    </Box>);

export const SEARCH_SHIPMENTS_ACCESS_DENIED_MESSAGE = "User does not have access to search Shipments!";

export const DOWNLOAD_SHIPMENTS_ACCESS_DENIED_MESSAGE = "User does not have access to download Shipments!";

export const CANCEL_REQUEST_ACCESS_DENIED_MESSAGE = "User does not have access to cancel this request!";

export const EXPEDITE_REQUEST_ACCESS_DENIED_MESSAGE = "User does not have access to expedite this shipment!";

export const FETCHING_EXPEDITE_ROUTE = "Fetching expedite route, please wait...";

export const ASN_UPLOAD_ACCESS_DENIED_MESSAGE = "User does not have access to upload Asn for this request!";

export const REQUEST_AUTHENTICATION_FAILURE_MESSAGE = "User is not authenticated to perform this request!";

export const REQUEST_DOCUMENT_UPLOAD_FAILURE_MESSAGE = "User is not authenticated to upload document";

export const CANCEL_REQUEST_INTERNAL_SERVER_ERROR_MESSAGE = "System error occurred while cancelling the request." +
                                                                " Our engineers are looking into it.";

export const EXPEDITE_REQUEST_INTERNAL_SERVER_ERROR_MESSAGE = "System error occurred while expediting this shipment." +
                                                                " Our engineers are looking into it.";

export const DOCUMENT_UPLOAD_INTERNAL_SERVER_ERROR_MESSAGE : string = "System error occurred while uploading documents."

export const ASN_UPLOAD_INTERNAL_SERVER_ERROR_MESSAGE = "System error occurred while uploading ASN for this shipment." +
    " Our engineers are looking into it.";

export const STD_ROUTE_FETCHING_ERROR_MESSAGE : string = "Please create Express Route manually via the ";

export const CANCEL_REQUEST_FAILURE_HEADER = "Request cancellation failed";

export const EXPEDITE_REQUEST_FAILURE_HEADER = "Expedite shipment failed";

export const ASN_UPLOAD_FAILURE_HEADER = "Asn Upload failed";

export const EXPEDITE_REQUEST_SUCCESS_HEADER = "Expedite shipment requested";

export const DOCUMENT_UPLOAD_SUCCESS_HEADER : string = "Document Upload Requested Successfully"

export const DOCUMENT_UPLOAD_FAILURE_HEADER : string = "Document Upload Failed"

export const DOCUMENT_UPLOAD_ACCESS_DENIED_HEADER : string = "Document Upload Not Allowed"

export const SYSTEM_ERROR_OCCURRED : string = "System Error Occurred";

export const CLASS_ID = {
    SITE_V3: 'SITE_V3',
    SUPPLY_CHAIN_NODE_V1: 'SUPPLY_CHAIN_NODE_V1',
    AVAILABILITY_ZONE: 'AVAILABILITY_ZONE',
    CLUSTER: 'CLUSTER'
};

// While adding a new type, also add it to the corresponding class in the below EQUIPMENT_TYPE_TO_CLASS map
export const EQUIPMENT_TYPE: Record<string, string> = {
    DC: 'DC',
    CM: 'CM',
    RAD: 'RAD',
    OUT: 'OUT',
    ISH: 'ISH',
    VMI: 'VMI',
    OEM: 'OEM',
    ODM: 'ODM',
    CLUSTER: 'CLUSTER',
    WAREHOUSE: 'WAREHOUSE',
    PAC_WAREHOUSE: 'PAC_WAREHOUSE',
    OUTPOST_WAREHOUSE: 'OUTPOST_WAREHOUSE',
    AVAILABILITY_ZONE: 'AVAILABILITY_ZONE'
};

export const SRC_TO_DEST_EQ_TYPES: Record<string, string[]> = {
    VMI: [EQUIPMENT_TYPE.VMI, EQUIPMENT_TYPE.OEM, EQUIPMENT_TYPE.ODM, EQUIPMENT_TYPE.CM, EQUIPMENT_TYPE.OUTPOST_WAREHOUSE, EQUIPMENT_TYPE.ISH, EQUIPMENT_TYPE.DC],
    OEM: [EQUIPMENT_TYPE.VMI, EQUIPMENT_TYPE.OEM, EQUIPMENT_TYPE.ODM, EQUIPMENT_TYPE.CM, EQUIPMENT_TYPE.OUTPOST_WAREHOUSE, EQUIPMENT_TYPE.ISH, EQUIPMENT_TYPE.DC],
    ODM: [EQUIPMENT_TYPE.VMI, EQUIPMENT_TYPE.OEM, EQUIPMENT_TYPE.ODM, EQUIPMENT_TYPE.CM, EQUIPMENT_TYPE.OUTPOST_WAREHOUSE, EQUIPMENT_TYPE.ISH, EQUIPMENT_TYPE.DC],
    CM:  [EQUIPMENT_TYPE.VMI, EQUIPMENT_TYPE.OEM, EQUIPMENT_TYPE.ODM, EQUIPMENT_TYPE.CM, EQUIPMENT_TYPE.OUTPOST_WAREHOUSE],
    ISH: [EQUIPMENT_TYPE.ISH],
    OUTPOST_WAREHOUSE: [EQUIPMENT_TYPE.VMI, EQUIPMENT_TYPE.OUTPOST_WAREHOUSE]
};

export const DEST_TO_SRC_EQ_TYPES: Record<string, string[]> = {};

for (const src in SRC_TO_DEST_EQ_TYPES) {
    for (const dest of (SRC_TO_DEST_EQ_TYPES[src])) {
        DEST_TO_SRC_EQ_TYPES[dest] = DEST_TO_SRC_EQ_TYPES[dest] || [];
        DEST_TO_SRC_EQ_TYPES[dest].push(src);
    }
}

export const EQUIPMENT_TYPE_TO_CLASS = new Map([
    [EQUIPMENT_TYPE.DC, CLASS_ID.SITE_V3],
    [EQUIPMENT_TYPE.OUT, CLASS_ID.SITE_V3],
    [EQUIPMENT_TYPE.CLUSTER, CLASS_ID.CLUSTER],
    [EQUIPMENT_TYPE.AVAILABILITY_ZONE, CLASS_ID.AVAILABILITY_ZONE],
    [EQUIPMENT_TYPE.CM, CLASS_ID.SUPPLY_CHAIN_NODE_V1],
    [EQUIPMENT_TYPE.RAD, CLASS_ID.SUPPLY_CHAIN_NODE_V1],
    [EQUIPMENT_TYPE.ISH, CLASS_ID.SUPPLY_CHAIN_NODE_V1],
    [EQUIPMENT_TYPE.VMI, CLASS_ID.SUPPLY_CHAIN_NODE_V1],
    [EQUIPMENT_TYPE.OEM, CLASS_ID.SUPPLY_CHAIN_NODE_V1],
    [EQUIPMENT_TYPE.ODM, CLASS_ID.SUPPLY_CHAIN_NODE_V1],
    [EQUIPMENT_TYPE.WAREHOUSE, CLASS_ID.SUPPLY_CHAIN_NODE_V1],
    [EQUIPMENT_TYPE.PAC_WAREHOUSE, CLASS_ID.SUPPLY_CHAIN_NODE_V1],
    [EQUIPMENT_TYPE.OUTPOST_WAREHOUSE, CLASS_ID.SUPPLY_CHAIN_NODE_V1]
]);

export const ASN_DESTINATION_NODE_TYPES = [
    EQUIPMENT_TYPE.DC,
    EQUIPMENT_TYPE.RAD,
    EQUIPMENT_TYPE.OUT
];

export const ERROR_TYPE = {
    NO_ERROR: 'NoError',
    CLIENT_ERROR: 'ClientError',
    ACCESS_DENIED: 'AccessDenied',
    SYSTEM_ERROR: 'SystemError'
};

export const ERROR_MESSAGE = {
    GENERIC_INTERNAL_SERVER_ERROR_MESSAGE: 'Internal Server Error',
    GENERIC_ACCESS_DENIED_ERROR_MESSAGE: 'Access Denied!',
    GENERIC_NOT_PERMITTED_ERROR_MESSAGE: 'Not Permitted!',
    GENERIC_ERROR_MESSAGE: 'Error occurred while processing your request. Our engineers are looking into this.',
    NOT_FOUND_STATUS_CODE: 404,
    ACCESS_DENIED_STATUS_CODE: 403,
    SAME_SOURCE_DESTINATION_LOCATION: 'Destination location cannot be same as the source location.',
    UPLOAD_S3_FILE_COMPONENT: {
        ALREADY_PRESENT_INVALID_S3_URI_ERR_MSG: 'Error occurred while loading the file. Please refresh the page.',
        GENERIC_S3_UPLOAD_ERR_MSG: 'Error occurred while uploading the file. Please, upload the file again or refresh the page.'
    },
    DATE_RANGE_ERRORS: {
        INVALID_PICKUP_DATE_ERROR_TEXT: 'Pickup date should always be in future.',
        INVALID_DATE_NOT_WITHIN_NEXT_90_DAYS_ERROR_TEXT: (date: string) => `Pickup date cannot exceed ${date}. It should always be within the next 90 days.`,
        INVALID_TIME_WITHIN_NEXT_45_MINUTES_ERROR_TEXT: (date: string) => `Pickup time cannot be before ${date}. It should always be after the next 45 minutes.`,
        INVALID_DELIVERY_DATE_ERROR_TEXT: (date: string) => `Requested delivery date cannot be before ${date}. It should always be in future.`,
        INVALID_DELIVERY_TIME_ERROR_TEXT: (date: string) => `Requested delivery time cannot be before ${date}. It should always be in future.`,
        INVALID_DELIVERY_TIME_RANGE_ERROR_TEXT: 'Requested delivery start time should be less than end time.',
        INVALID_DELIVERY_DATE_NOT_ON_SAME_DAY_ERROR_TEXT: 'Requested delivery date window should always lie within the same day.'
    },
    SHIP_REQT_PROD_SPACE_ERRORS: {
        NOT_FOUND_ERR: {
            ALERT_MESSAGE: (value: string) => `Shipping request with id: ${value} not found`
        },
        INTERNAL_SERVER_ERROR: {
            ALERT_MESSAGE: (<Box padding="n" color="inherit">
                Error fetching shipping request details.
                Please refresh the page.
            </Box>)
        }
    },
    SHIP_PKG_PROD_SPACE_ERRORS: {
        INTERNAL_SERVER_ERROR: {
            ALERT_MESSAGE: (<Box padding="n" color="inherit">
                Error fetching the shipment tracking details.
                Please refresh the page.
            </Box>)
        }
    },
    EDIT_ASN_PAGE: {
        EDI_REQUEST_IS_NOT_EDITABLE: 'User can not edit EDI requests.',
        REQUEST_IS_NOT_EDITABLE: 'The request is currently processing, please wait for the processing to complete to edit this request.',
        REQUEST_STATE_IS_NOT_EDITABLE: (state: string, isBookingRequest: boolean) => `User can not edit a request in ${state} state. Edit is allowed only for [${ShippingRequestState.fetchAllEditableStates(isBookingRequest)}] states.`,
        INTERNAL_SERVER_ERROR: {
            ALERT_MESSAGE: (<Box padding="n" color="inherit">
                Error fetching shipping details.
                {CONTACT_DEV_TEAM_MESSAGE}
            </Box>)
        }
    },
    SEARCH_SHIPMENTS: {
        MAX_SEARCH_LIMIT_ERROR_TEXT: 'Maximum search length crossed. Supported number of comma-separated values is 300.',
        INVALID_FILTERS_ERROR_TEXT: 'Please enter valid values in the below filters.',
        INVALID_RANGE_INPUT_ERROR_TEXT: 'Start date should be less than end date.',
        ERROR_SUPPLIER_REFERENCE_DROP_DOWN: 'Error while fetching supplier information',
        ERROR_CLUSTER_DROP_DOWN: 'Error while fetching cluster information',
        ERROR_AZ_DROP_DOWN: 'Error while fetching AZ information',
        ERROR_SOURCE_LOCATION_DROP_DOWN: 'Error while fetching source locations',
        ERROR_DESTINATION_LOCATION_DROP_DOWN: 'Error while fetching destination locations',
        ERROR_CONFIGURATIONS: 'Error while fetching ',
        SYSTEM_ERROR: 'Error occurred while searching for shipments. Please refresh the page.',
        NO_QUERY_ENTERED: 'No search criterion selected, Please enter any search reference or select the filters for search.'
    },
    DOWNLOAD_SHIPMENTS: {
        SYSTEM_ERROR: 'Error occurred while downloading shipments. Please try again'
    },
    LIST_SUBSCRIPTIONS: {
        SYSTEM_ERROR: 'Error occurred while fetching shipments subscription status. Please refresh the page.'
    },
    USER_SPACE_ERRORS: {
        NOT_FOUND_ERR: {
            NAME: 'NotFoundException',
            ALERT_MESSAGE: "Tracking details not available"
        },
        ACCESS_DENIED_ERR: {
            NAME: 'AccessDeniedException',
        },
        INVALID_INPUT_ERR: {
            NAME: 'InvalidInputException',
        },
        INTERNAL_SERVER_ERROR: {
            ALERT_MESSAGE: (<Box padding="n" color="inherit">
                Error fetching tracking details.
                {CONTACT_DEV_TEAM_MESSAGE}
            </Box>)
        }
    },
    UPLOAD_ASN_PAGE: {
        INTERNAL_SERVER_ERROR: {
            ALERT_MESSAGE: (<Box padding="n" color="inherit">
                Error while uploading ASN.
                {CONTACT_DEV_TEAM_MESSAGE}
            </Box>)
        }
    },
    CREATE_BOOKING_PAGE: {
        INTERNAL_SERVER_ERROR: {
            ALERT_MESSAGE: (<Box padding="n" color="inherit">
                Error while uploading packaging details.
                {CONTACT_DEV_TEAM_MESSAGE}
            </Box>)
        },
        SHIPMENT_PACKAGE_DETAILS_MANDATORY: "Shipment package details are mandatory for air and ground Shipments.",
        EQUIPMENT_TYPE_MANDATORY: "EquipmentType mandatory for ocean shipments."
    },
};

export const SHIPPING_REQUEST_STATUS_BANNER = {
    TRACKING_REQUEST_MESSAGE: (requestId: string) => <Box padding="n" variant="span" color="inherit">Use shipment reference number <u><strong>{requestId}</strong></u> for tracking.</Box>,
    SUBMITTED : {
        HEADER: "Validating package details",
        MESSAGE: "Please remain on this page until complete."
    },
    PROCESSING : {
        HEADER: "Validation complete. Processing input request.",
        CREATE_MESSAGE: "Please remain on this page until complete.",
        CREATE_PROCESSING_WITH_WARNINGS_MESSAGE: "Stay on this page while your input request is processed." +
            " Packaging details are validated with warnings. Download the file to inspect warnings.",
        UPDATE_MESSAGE: "Please remain on this page until complete.",
        UPDATE_PROCESSING_WITH_WARNINGS_MESSAGE: "Stay on this page while your input request is processed." +
            " Packaging details are validated with warnings. Download the file to inspect warnings.",
        CANCEL_HEADER: "Canceling request",
        CANCEL_MESSAGE: "Please remain on this page until complete."
    },
    ERROR : {
        HEADER: "There's a system issue",
        MESSAGE: "We couldn't process your request. Please try again later."
    },
    SHIPMENT_PLANNED : {
        HEADER: "Your request is being processed.",
        MESSAGE: "Please remain on this page until complete."
    },
    TENTATIVE : {
        MESSAGE: "The booking is in draft status and its attributes might be modified until the delivery date falls within the lock-in period."
    },
    LOAD_TENDERED : {
        HEADER: "Booking request sent to the carrier for confirmation."
    },
    LOAD_TENDER_CANCELLED : {
        HEADER: "Load tender cancelled",
        MESSAGE: "The load tender for this booking has been cancelled due to false ready to ship signal received from manufacturer." +
            " For any escalations/query, please reach out to your AWS representative, and use Asset ID as reference number."
    },
    BOOKING_REQUESTED : {
        HEADER: "Booking request submitted",
        MESSAGE: "Your request will be sent to the carrier for confirmation. Please wait until a carrier is assigned before reaching out."
    },
    BOOKING_ACCEPTED : {
        HEADER: "Booking request accepted by the carrier."
    },
    ON_HOLD : {
        HEADER: "Booking on hold",
        MESSAGE: (reasons: any, additionalInfo: string, showAwstInfo: boolean = true) => {
            if (reasons === null) {
                return null;
            }
            return ( 
                <Box padding="n" variant="span" color="inherit"> Booking has been put on hold due to following reasons: 
                {reasons} {showAwstInfo && "AWST has received a ticket to enable pending AWS approvals. "}
                {additionalInfo} {showAwstInfo && "For escalations, please reach out to your AWS representative, and use your AWS shipment ID as reference number."} </Box> );
        }
    },
    COMPLIANCE_HOLD : {
        HEADER : "Booking is on Compliance Hold",
        MESSAGE: (reasons: any) => {
            if (reasons === null) {
                return (
                    <Box padding="n" variant="span" color="inherit"> Booking is currently on Compliance Hold due to transportation related documentation approval pending. </Box> );
            }
            return (
                <Box padding="n" variant="span" color="inherit"> Booking is currently on compliance hold due to following reasons :
                    {reasons} </Box> );
        }
    },
    RACK_BOOKING_SUBMITTED : {
        HEADER: "Booking submitted",
        MESSAGE: "This booking has been submitted. However, asset is not ready for pickup by carrier yet. " + 
                "Please wait until supplier mark it as ready to be shipped.",
        TENDER_CANCELLED_MESSAGE: "This booking has been rolled back to submitted due to false ready to ship signal sent by supplier. " + 
                "Awaiting confirmation on asset's state before re-tendering to the carrier."
    },
    RACK_BOOKING_PENDING_ASSIGNMENT : {
        HEADER: "Booking Pending Assignment",
        MESSAGE: "This booking has been put on-hold due to Order Cancellation" +
            " For any escalations/query, please reach out to your AWS representative, and use Asset ID as reference number."
    },
    RACK_BOOKING_REDIRECTED : {
        HEADER: "Booking Redirected",
        MESSAGE: "This booking has been redirected in reference to a PurchaseOrder" +
            " For any escalations/query, please reach out to your AWS representative, and use Asset ID as reference number."
    },
    BOOKING_REJECTED : {
        HEADER: "Booking request rejected by the carrier."
    },
    FAILURE : {
        HEADER: "We couldn't validate your input request.",
        MESSAGE: (requestId: string) =>
            <Box variant="span">Download the file to address validation errors and try again. {SHIPPING_REQUEST_STATUS_BANNER.TRACKING_REQUEST_MESSAGE(requestId)}</Box>,
        UPDATE_HEADER: "We couldn't validate your update request.",
        UPDATE_MESSAGE: (requestId: string) =>
            <Box variant="span">Download the file to address validation errors and try again. {SHIPPING_REQUEST_STATUS_BANNER.TRACKING_REQUEST_MESSAGE(requestId)}</Box>
    },
    ACCEPTED : {
        HEADER: "Request processed",
        MESSAGE: (requestId: string) => <Box variant="span">{SHIPPING_REQUEST_STATUS_BANNER.TRACKING_REQUEST_MESSAGE(requestId)}</Box>,
        ACCEPTED_WITH_WARNINGS_MESSAGE: (requestId: string) =>
            <Box variant="span">The request processed with warnings. Download the file to address warnings. {SHIPPING_REQUEST_STATUS_BANNER.TRACKING_REQUEST_MESSAGE(requestId)}</Box>
    },
    CANCELLED : {
        HEADER: "Request canceled"
    }
};


export const WAREHOUSE_BOOKING_BANNER = {
    HEADER: "Warehouse Movement",
    MESSAGE: "This move has been created to track movement of shipment within our facilities. " +
        "No external transportation is involved in this process."
}

export const MIGRATED_BOOKING_BANNER = {
    HEADER: "Migrated Booking",
    MESSAGE: "This booking was migrated from the legacy shipment plan to the TMS. " +
        "The displayed dates are derived from historical system data and may differ from real-time tracking updates."
}

export const COMING_SOON_FLASHBAR_HEADER = 'Coming soon!';

export const EXTERNAL_USER_NOT_ALLOWED_FLASHBAR_HEADER = 'Access Denied';

export const COMING_SOON_FLASHBAR_MESSAGE = 'We are currently working on development of this page';

export const EXTERNAL_USER_NOT_ALLOWED_FLASHBAR_MESSAGE = 'External user not allowed access for this page';

export const DOWNLOAD_FILE_STATUS_MESSAGES = {
    DOWNLOAD_VALIDATION_WARNING_ERROR_MESSAGE: 'Error while downloading validation warning report',
    DOWNLOAD_VALIDATION_ERROR_MESSAGE: 'Error while downloading validation failure report',
    IN_PROGRESS_MESSAGE: 'Please wait while we download your file',
    NO_S3URI_MESSAGE: 'No file found to download',
    DOWNLOAD_PACKAGING_REFERENCE_ERROR_MESSAGE: 'Error while downloading packaging details file',
    DOWNLOAD_ASN_TEMPLATE_ERROR_MESSAGE: 'Error while downloading ASN template',
    DOWNLOAD_BOOKING_TEMPLATE_ERROR_MESSAGE: 'Error while downloading Booking template',
    DOWNLOAD_USER_GUIDE_ERROR_MESSAGE: 'Error while downloading User Guide',
    DOWNLOAD_TERMS_AND_CONDITIONS_ERROR_MESSAGE: 'Error while downloading the Terms and Conditions',
    DOWNLOAD_WEBINAR_VIDEO_ERROR_MESSAGE: 'Error while downloading webinar video',
    DOWNLOAD_SHIPMENT_PLANS_ERROR_MESSAGE: 'Error while downloading Shipment Plans',
    DOWNLOAD_BULK_ACTION_TEMPLATE_ERROR_MESSAGE: "Error while downloading template file"
};

export const NAVIGATION_PANE = {
  SECTION_TITLE: 'Shipments',
  TRACK_SHIPMENTS: 'Track shipments',
  UPLOAD_ASN: 'Upload an ASN',
  CREATE_BOOKING: 'Create a booking',
  UPDATE_BOOKING: 'Update booking',
  SHIPMENT_DELIVERY_FORECAST: 'Shipment Delivery Forecast',
  ASSET_TDD_HISTORY: 'Asset TDD History',
  DMS: 'Dock management',
  DOWNLOADS: 'Downloads',
  LMDP: 'Last Mile Delivery Planner',
  PLANNED_DELIVERIES: 'Planned Deliveries',
  CABLING_FORECAST: 'Cabling Manifest',
  DELIVERY_FORECAST: 'Delivery Forecast',
  BLOCKED_DELIVERIES: 'Throughput Blocked Deliveries',
  LAST_MILE_HUB_VIEW: 'Last Mile Hub View',
  SHIPPING_CONFIGURATIONS: 'Shipping Configurations',
  SIMULATIONS: 'Shipment plan simulator',
  BULK_ACTIONS: 'Bulk Actions'
};

export const URLS = {
  PAGES: {
      TRACK_SHIPMENTS: '/',
      UPLOAD_ASN: '/upload/asn',
      CREATE_BOOKING: '/create/booking',
      SHIPMENTS_DETAILS: '/shipments/details/',
      DMS_SEARCH: '/dms/search',
      DMS_BOOKING_DETAILS: '/shipments/dms/booking/:bookingId',
      DMS_BOOKING_SITE_LOAD: '/shipments/dms/booking/:bookingId/load/:site',
      DMS_BOOKING_SITE_SUMMARY: '/shipments/dms/booking/:bookingId/summary/:site',
      TOA_REQUEST_URL: '/asset-transfer/toa-request/winston_wolfe/:toaRequestId',
      SHIPMENT_DELIVERY_FORECAST_URL: 'https://w.amazon.com/bin/view/AWS_SCM/Shipment/2.0/Dashboard/Quicksight/ForecastedView',
      ASSET_TDD_HISTORY_URL: 'https://w.amazon.com/bin/view/AWS_SCM/Shipment/2.0/Dashboard/Quicksight/AssetAuditHistory',
      SEARCH_ERROR_DATA: '/search/error/data',
      PLANNED_DELIVERIES: '/lmdp/planned_deliveries',
      DELIVERY_FORECAST: '/lmdp/delivery_forecast',
      THREE_WEEK_CABLING_FORECAST: '/lmdp/cabling_manifest',
      BLOCKED_DELIVERIES: '/lmdp/blockedDeliveries',
      LAST_MILE_HUB_VIEW: '/lmdp/last_mile_hub_view',
      SHIPPING_CONFIGURATION_URL: '/configuration/',
      SIMULATIONS: '/simulations/',
      SHIPPING_CONFIGURATION_EDIT_URL: '/configuration/edit',
      THROUGHPUT_BLOCKED_DELIVERY: 'https://w.amazon.com/bin/view/AWS_SCM/Shipment/2.0/Dashboard/Quicksight/ThroughputBlockedDeliveries/',
      SHIPPING_CONFIGURATIONS_CREATE_URL: '/shipments/configuration/create',
      SHIPPING_CONFIGURATIONS_SEARCH_URL: '/shipments/configuration/search',
      DOWNLOADS: '/downloads/',
      BULK_ACTIONS: '/bulk-actions',
      BULK_EDIT: '/bulk-edit'
  }
};

export const HELP_PANE = {
    DESCRIPTION : "Shipments portal lets you upload ASN and search for any shipment information including package details."
};

export const LOADING = "loading";

export const ERROR = "error";

export const FINISHED = "finished";

export const ARROW = " -> ";

export const MISSING_ROUTE = {
    CONFLICTING_ROUTE_HEADER : "Conflicting Routes",
    CONFLICTING_ROUTE_CONTENT : "Conflicting Routes from %s to %s",
    MISSING_ROUTE_HEADER : "Route Missing",
    MISSING_REDIRECT_ROUTE_HEADER : "Redirect Route Missing",
    MISSING_ROUTE_CONTENT : "Route is Missing from %s to %s",
    BLOCKED_MESSAGE : "Blocked",
}

export const PKG_STATES = {
    SCHEDULED: "Scheduled",
    IN_TRANSIT: "In-transit",
    DELIVERED: "Delivered",
    ON_HOLD: "On-hold",
    RETURNED: "Returned",
    CANCELLED: "Cancelled",
    CREATED: "Created"
};

export const SHIPMENT_TRACKER = {
    SHIPPED_LABEL: "Shipped",
    ACTUAL_EVENT_PREFIX: "on ",
    ESTIMATED_EVENT_PREFIX: "Expected by ",
    PENDING_DELIVERY: "Pending delivery",
    PENDING_PICK_UP: "Pending pick-up"
};

export const ASN_FILTER_GROUP_NAME = "ASN";

export const SHIPMENTS_FILTER_GROUP_NAME = "SHIPMENTS";

export const REQUEST_STATUS: Map<String, String> = new Map([...SHIPMENT_STATUS_FILTER, ...BOOKING_STATUS_FILTER].map(entry => [entry.id, entry.label]));

export const EMPTY_UPDATE_REASON_CODES_STRING = "No update applied to booking";
export const EMPTY_STRING = "";
export const ADDITIONAL_COMMENTS_PLACEHOLDER_MESSAGE = "Additional comments for updating the booking";

// Manual Hold Reason Codes
export const MANUAL_HOLD_REASON_CODES = [
    "Pending Business Decision",
    "Pending Documents",
    "Manufacturing Defect (Pre-Shipping)",
    "Rack Quality Defect Investigation (post-Shipping)",
    "CDR - Project Cancelled",
    "CDR - Redirect",
    "CDR - Rack Quality Defect (HW Eng)",
    "ACL - Rack Quality Defect (LSP/ODM Investigation)",
    "ACL - Force Majeure",
    "EDGE - Pending Delivery Appointment",
    "DC - Rejection"
];

//Manual Resume Reason Code
export const MANUAL_RESUME_REASON_CODES = [
    "Manual Resume",
    "MANUAL_RESUME"
];

// Combined Workflow Requestor
export const SYSTEM_GENERATED_COMBINE_WORKFLOW = "SYSTEM_GENERATED_COMBINE_WORKFLOW";

export const QUERY_PARAMS = '{queryParams}';
export const EQUALS = "=";
export const AMPERSAND = "&";

export const PARSING_ERROR_LOG = (json: string, extraLog: string, errorMessage: any) => `Error occurred while parsing Json: ${json}. Following extra details were provided: ${extraLog} and the error is: ${errorMessage}`;
export const ERROR_METRIC_NAME = "ERROR";
export const JSON_PARSE_METHOD_NAME = "JSON_PARSING";

export const SHIPMENT_REQUEST_ACCESS_TICKET_LINK = "https://t.corp.amazon.com/create/options?category=AWS&description=Team+URL%3A+https%3A%2F%2Fpermissions.amazon.com%2Fa%2Fteam%2Faws-scm-portal-prod-shipment-users&item=Shipments&title=Membership+request+for+team+%22aws-scm-portal-prod-shipment-users%22&type=Supply+Chain+Management";